import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Marker, Circle } from "react-leaflet";
import { toast } from "sonner";
import { Crosshair } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { WomaMap } from "./WomaMap";
import { roleIcon, ROLE_COLORS } from "./roleMarkers";
import { useGeolocation } from "@/hooks/useGeolocation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Artisan : zone d'intervention + disponibilité visibles des clients du quartier
export const ArtisanMapPanel = ({ user }) => {
  const { position, status, request } = useGeolocation();
  const [profile, setProfile] = useState(null);
  const icon = useMemo(() => roleIcon("artisan", { size: 44 }), []);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/artisans/profile/${user.id}`).then((r) => setProfile(r.data)).catch(() => setProfile({}));
  }, [user?.id]);

  const base = profile?.base_latitude && profile?.base_longitude ? { lat: profile.base_latitude, lng: profile.base_longitude } : null;
  const available = profile?.is_available !== false;

  const save = async (coords, isAvailable = available) => {
    if (!coords) {
      toast.info("Localisation en cours… autorisez le GPS puis réessayez");
      return request();
    }
    try {
      await axios.put(`${API}/users/me/location`, { latitude: coords.lat, longitude: coords.lng, role: "artisan", is_available: isAvailable });
      setProfile((p) => ({ ...p, base_latitude: coords.lat, base_longitude: coords.lng, is_available: isAvailable }));
      toast.success(isAvailable ? "Vous êtes visible des clients de votre quartier" : "Vous êtes masqué de la recherche");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Créez d'abord votre profil artisan");
    }
  };

  return (
    <section className="space-y-3" data-testid="artisan-map-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#D4AF37]">Ma zone d'intervention</p>
          <p className="text-[11px] text-slate-400 mt-1">Rayon {profile?.service_radius_km || 15} km autour de votre base</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-[11px] font-bold text-slate-300">
            <Switch checked={available} onCheckedChange={(v) => save(base || position, v)} className="data-[state=unchecked]:bg-white/25" data-testid="artisan-map-available-switch" />
            {available ? "Disponible" : "Indisponible"}
          </label>
          <button type="button" onClick={() => save(position)} className="rounded-full bg-[#D4AF37] text-[#0F172A] px-4 py-2 text-[11px] font-black uppercase tracking-widest flex items-center gap-2 active:scale-95 transition-transform" data-testid="artisan-map-set-base-btn">
            <Crosshair size={14} /> Ma base ici
          </button>
        </div>
      </div>
      <WomaMap self={position} role="dot" status={base ? null : status} onRequest={request} testId="artisan-map">
        {base && (
          <>
            <Circle center={[base.lat, base.lng]} radius={(profile?.service_radius_km || 15) * 1000} pathOptions={{ color: ROLE_COLORS.artisan, fillOpacity: available ? 0.12 : 0.03, weight: 1.5 }} />
            <Marker position={[base.lat, base.lng]} icon={icon} />
          </>
        )}
      </WomaMap>
    </section>
  );
};
